import React, { useMemo, useState } from "react";
import { CustomIcon } from "../shared/CustomIcon";

// =============================================================
// AssignMemberModal — searchable member picker for assigning a
// member to a vacant tree node. Filters out members already in
// active tree slots (server also enforces).
// =============================================================
export default function AssignMemberModal({ node, members, activeAssignments, onConfirm, onClose, submitting }) {
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (members || []).map(m => {
      const already = !!activeAssignments?.[m.id];
      return { ...m, _already: already };
    }).filter(m => {
      if (!term) return true;
      const name = ((m.firstName || "") + " " + (m.lastName || "")).toLowerCase();
      const num = String(m.memberNumber || "");
      return name.includes(term) || num.includes(term.replace(/^#/, ""));
    }).slice(0, 60);
  }, [q, members, activeAssignments]);

  const selected = selectedId ? (members || []).find(m => m.id === selectedId) : null;
  const canConfirm = !!selected && !selected._already && !submitting;

  return (
    <div className="modal-overlay" data-testid="assign-modal" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title" data-testid="assign-modal-title">Assigning to {node.slotRef} — {node.levelName}</div>
            <div style={{ fontSize: 11, color: "var(--kc-sub)", marginTop: 4, fontFamily: "'Montserrat',sans-serif" }}>
              Level {node.level}
            </div>
          </div>
          <button className="modal-close" onClick={onClose} data-testid="assign-close" aria-label="Close">×</button>
        </div>
        <div className="modal-body">
          <label style={{ fontSize: 11, color: "var(--kc-sub)", textTransform: "uppercase", letterSpacing: "0.08em", fontWeight: 700 }}>Search by name or member number</label>
          <input
            data-testid="assign-search-input"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. John, or 0000047"
            className="form-input"
            style={{ width: "100%", marginTop: 6, marginBottom: 12 }}
          />
          <div data-testid="assign-results" style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 300, overflowY: "auto", paddingRight: 4 }}>
            {results.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--kc-sub)", fontSize: 12, fontFamily: "'Montserrat',sans-serif" }}>
                No members match.
              </div>
            )}
            {results.map(m => {
              const isSel = selectedId === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`assign-result-${m.id}`}
                  disabled={m._already}
                  onClick={() => setSelectedId(m.id)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                    borderRadius: 10, cursor: m._already ? "not-allowed" : "pointer",
                    background: isSel ? "color-mix(in srgb, var(--kc-accent) 15%, transparent)" : "transparent",
                    border: `1px solid ${isSel ? "var(--kc-accent)" : "var(--kc-sub)"}`,
                    opacity: m._already ? 0.5 : 1, textAlign: "left", color: "inherit", font: "inherit",
                  }}
                >
                  {m.photo ? (
                    <img src={m.photo} alt="" style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                  ) : (
                    <div style={{ width: 34, height: 34, borderRadius: "50%", background: "var(--kc-accent)", color: "#111", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12, flexShrink: 0 }}>
                      {(m.firstName?.[0] || "?") + (m.lastName?.[0] || "")}
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--kc-text)" }}>
                      {(m.firstName || "") + " " + (m.lastName || "")}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--kc-accent)", fontFamily: "'Cinzel',serif" }}>
                      #{m.memberNumber || "—"} · {m.status || "Active"}
                    </div>
                  </div>
                  {m._already && (
                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", padding: "3px 8px", borderRadius: 999, color: "var(--kc-sub)", border: "1px solid rgba(255,255,255,0.20)", background: "rgba(255,255,255,0.04)" }}>
                      Already in tree
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline btn-sm" onClick={onClose} data-testid="assign-cancel">Cancel</button>
          <button
            type="button"
            className="btn btn-gold btn-sm"
            data-testid="assign-confirm"
            disabled={!canConfirm}
            onClick={() => selected && onConfirm(selected)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <CustomIcon name="check" size={12} /> {submitting ? "Assigning…" : "Assign Member"}
          </button>
        </div>
      </div>
    </div>
  );
}
